import { TOUR_IDS } from "@bcc/components/tour/tour-ids";
import { BatchSummary } from "@bcc/components/batches/batch-summary";
import { BatchesTable } from "@bcc/components/batches/batches-table";
import { Badge } from "@bcc/components/ui/badge";
import { Button } from "@bcc/components/ui/button";
import type { CarrierCustomerAccountBinding, LabelPrintBatch, LabelPrintBatchCreateRequest, LabelPrintBatchDetail } from "@shared/types";
import * as React from "react";
import { useTranslation } from "react-i18next";
import {
  BATCH_STATUS_FILTER_VALUES,
  type BatchStatusFilterValue,
  getVisibleBatches,
  setBatchesViewActiveQueryTestId,
  setBatchesViewFilterTestId,
  setBatchesViewSummaryTestId,
  setBatchesViewTestId,
} from "./batches-view.utils";

export interface BatchesViewProps {
  batchesData: LabelPrintBatch[];
  batch?: LabelPrintBatchDetail | null;
  customerAccountCarrierDetails?: CarrierCustomerAccountBinding[];
  isLoading?: boolean;
  isRetrying?: boolean;
  isGeneratingManifest?: boolean;
  searchValue?: string;
  statusFilter?: BatchStatusFilterValue;
  selectedBatchId?: string | null;
  onSelectBatch?: (batchId?: string) => void;
  onStatusFilterChange?: (status: BatchStatusFilterValue) => void;
  onGenerateManifest?: (batch: LabelPrintBatchDetail) => void;
  onReprintLabels?: (batch: LabelPrintBatchDetail) => void;
  onReprintPackingSlips?: (batch: LabelPrintBatchDetail) => void;
  onReprintLabelsAndPackingSlips?: (batch: LabelPrintBatchDetail) => void;
  onOpenSummary?: (batch: LabelPrintBatchDetail) => void;
  onRetryBatch?: (request: LabelPrintBatchCreateRequest) => void | Promise<void>;
  testId?: string;
}

const getStatusLabelKey = (status: BatchStatusFilterValue): string => status;

export function BatchesView({
  batchesData,
  batch,
  customerAccountCarrierDetails,
  isLoading = false,
  isRetrying = false,
  isGeneratingManifest = false,
  searchValue = "",
  statusFilter = "all",
  selectedBatchId,
  onSelectBatch,
  onStatusFilterChange,
  onGenerateManifest,
  onReprintLabels,
  onReprintPackingSlips,
  onReprintLabelsAndPackingSlips,
  onOpenSummary,
  onRetryBatch,
  testId,
}: Readonly<BatchesViewProps>) {
  const { t } = useTranslation();

  const visibleBatches = React.useMemo(
    () =>
      getVisibleBatches({
        batches: batchesData,
        searchValue,
        statusFilter,
      }),
    [batchesData, searchValue, statusFilter]
  );

  React.useEffect(() => {
    if (isLoading) {
      return;
    }

    if (visibleBatches.length === 0) {
      if (selectedBatchId && batchesData.length > 0) {
        onSelectBatch?.(undefined);
      }
      return;
    }

    const hasSelectedVisibleBatch = visibleBatches.some(({ batchId }) => batchId === selectedBatchId);

    if (!hasSelectedVisibleBatch) {
      onSelectBatch?.(visibleBatches[0]?.batchId);
    }
  }, [batchesData.length, isLoading, onSelectBatch, selectedBatchId, visibleBatches]);

  const selectedVisibleBatch = React.useMemo(() => {
    if (!batch) {
      return null;
    }

    return visibleBatches.some(({ batchId }) => batchId === batch.batchId) ? batch : null;
  }, [batch, visibleBatches]);

  const isSelectedBatchLoading = Boolean(isLoading && selectedBatchId && selectedVisibleBatch?.batchId !== selectedBatchId);

  const hasSearchValue = Boolean(searchValue.trim());
  const hasStatusFilter = statusFilter !== "all";

  return (
    <div data-testid={testId ?? setBatchesViewTestId("container")} className="space-y-6">
      <div className="rounded-lg border border-border bg-card p-4">
        <div className="flex flex-col gap-4 lg:flex-row lg:items-center lg:justify-between">
          <div className="flex flex-wrap items-center gap-2" data-tour-id={TOUR_IDS.batches.statusFilters}>
            <Button
              type="button"
              size="sm"
              variant={statusFilter === "all" ? "default" : "outline"}
              data-testid={setBatchesViewFilterTestId("all")}
              onClick={() => onStatusFilterChange?.("all")}
            >
              {t("views.batches.filters.all", { defaultValue: "All" })}
            </Button>

            {BATCH_STATUS_FILTER_VALUES.map((status) => (
              <Button
                key={status}
                type="button"
                size="sm"
                variant={statusFilter === status ? "default" : "outline"}
                data-testid={setBatchesViewFilterTestId(status)}
                onClick={() => onStatusFilterChange?.(status)}
              >
                {t(`views.batchStatus.${getStatusLabelKey(status)}`)}
              </Button>
            ))}
          </div>

          <div className="text-sm text-muted-foreground" data-testid={setBatchesViewSummaryTestId()}>
            {t("views.batches.results", {
              count: visibleBatches.length,
              defaultValue: "{{count}} batches",
            })}
          </div>
        </div>

        {(hasSearchValue || hasStatusFilter) && (
          <div className="mt-4 flex flex-wrap items-center gap-2" data-testid={setBatchesViewActiveQueryTestId()}>
            {hasSearchValue && (
              <Badge variant="secondary">
                {t("views.batches.searchBadge", {
                  value: searchValue,
                  defaultValue: 'Search: "{{value}}"',
                })}
              </Badge>
            )}

            {hasStatusFilter && (
              <Badge variant="outline">
                {t("views.batches.statusBadge", {
                  value: t(`views.batchStatus.${getStatusLabelKey(statusFilter)}`),
                  defaultValue: "Status: {{value}}",
                })}
              </Badge>
            )}
          </div>
        )}
      </div>

      <BatchSummary
        batch={selectedVisibleBatch}
        customerAccountCarrierDetails={customerAccountCarrierDetails}
        isLoading={isLoading && (!selectedVisibleBatch || isSelectedBatchLoading)}
        isRetrying={isRetrying}
        isGeneratingManifest={isGeneratingManifest}
        onGenerateManifest={onGenerateManifest}
        onReprintLabels={onReprintLabels}
        onReprintPackingSlips={onReprintPackingSlips}
        onReprintLabelsAndPackingSlips={onReprintLabelsAndPackingSlips}
        onOpenSummary={onOpenSummary}
        onRetryBatch={onRetryBatch}
      />

      <div data-tour-id={TOUR_IDS.batches.table}>
        <BatchesTable data={visibleBatches} isLoading={isLoading} selectedBatch={selectedBatchId} onSelectBatch={onSelectBatch} />
      </div>
    </div>
  );
}
